import { z } from 'zod';

import {
  EntityBaseSchema,
  EntityMetadataSchema,
  NonEmptyStringSchema,
  ReferenceSchema,
} from '../../base';

// Entity-specific error messages
export const ENTITY_ERRORS = {
  required: 'This field is required.',
  max: (value: number) => `It must be under ${value} characters.`,
};

// Entity field validators
export const EntityNameSchema = NonEmptyStringSchema;
export const EntityDescriptionSchema = NonEmptyStringSchema;

export enum EntityType {
  // legacy entities
  STRING = 'string',
  NUMBER = 'number',
  INTEGER = 'integer',
  BOOLEAN = 'boolean',

  // new entities
  ADDRESS = 'address',
  ALPHANUMERIC = 'alphanumeric',
  DATE = 'date',
  EMAIL = 'email',
  NUMERIC = 'numeric',
  PHONE_NUMBER = 'phoneNumber',
  TIME = 'time',
  ENUM = 'enum',
  CURRENCY = 'currency',
  QUANTITY = 'quantity',
  NAME = 'nameConfig',
  FREE_TEXT = 'freeText',
}

// Entity type enum schema
export const EntityTypeEnums = z
  .nativeEnum(EntityType)
  .describe(
    'The data type for this entity, determines validation and ASR biasing behavior',
  );

// Entity references schema
export const EntityReferencesSchema = z.object({
  flowSteps: ReferenceSchema,
  noCodeSteps: ReferenceSchema,
});

export type EntityReferences = z.infer<typeof EntityReferencesSchema>;

// Entity configuration schemas
export const NumberSchema = z
  .object({
    $case: z.literal(EntityType.NUMERIC),
    value: z.object({
      hasDecimal: z.boolean().describe('Whether decimal values are accepted'),
      hasRange: z.boolean().describe('Whether to enforce min/max range'),
      min: z.number().min(0).optional(),
      max: z.number().max(1000000).optional(),
    }),
  })
  .describe('Numeric entity with optional decimal and range validation');

export type NumberConfig = z.infer<typeof NumberSchema>;

export const TimeSchema = z
  .object({
    $case: z.literal(EntityType.TIME),
    value: z.object({
      enabled: z.boolean().describe('Whether time range restriction is active'),
      startTime: z.string().describe('Earliest accepted time'),
      endTime: z.string().describe('Latest accepted time'),
    }),
  })
  .describe('Time-of-day entity with optional range restriction');

export type TimeConfig = z.infer<typeof TimeSchema>;

export const PhoneNumberSchema = z
  .object({
    $case: z.literal(EntityType.PHONE_NUMBER),
    value: z.object({
      enabled: z
        .boolean()
        .describe('Whether country code restriction is active'),
      countryCodes: z
        .array(z.string())
        .describe('Accepted country codes (e.g., US, UK)'),
    }),
  })
  .describe('Phone number entity with optional country code validation');

export type PhoneNumberConfig = z.infer<typeof PhoneNumberSchema>;

export const AlphanumericSchema = z
  .object({
    $case: z.literal(EntityType.ALPHANUMERIC),
    value: z.object({
      enabled: z.boolean().describe('Whether regex validation is active'),
      validationType: z
        .string()
        .describe(
          "Validation strategy: 'regex' for custom pattern, 'email' for email, 'none' for no validation",
        ),
      regularExpression: z
        .string()
        .describe('Regex pattern for input validation'),
    }),
  })
  .describe(
    'Alphanumeric entity for mixed text like booking refs, postcodes, or emails',
  );

export type AlphanumericConfig = z.infer<typeof AlphanumericSchema>;

export const MultipleOptionsSchema = z
  .object({
    $case: z.literal(EntityType.ENUM),
    value: z.object({
      options: z.array(z.string()).describe('List of accepted values'),
    }),
  })
  .describe('Enum entity for selecting from predefined options');

export type MultipleOptionsConfig = z.infer<typeof MultipleOptionsSchema>;

export const AddressSchema = z
  .object({
    $case: z.literal(EntityType.ADDRESS),
    value: z.object({}),
  })
  .describe('Physical address entity');

export type AddressConfig = z.infer<typeof AddressSchema>;

export const FreeTextSchema = z
  .object({
    $case: z.literal(EntityType.FREE_TEXT),
    value: z.object({}),
  })
  .describe('Unstructured free text entity with no validation');

export type FreeTextConfig = z.infer<typeof FreeTextSchema>;

export const NameSchema = z
  .object({
    $case: z.literal(EntityType.NAME),
    value: z.object({}),
  })
  .describe('Person name entity');

export type NameConfig = z.infer<typeof NameSchema>;

export const DateEntitySchema = z
  .object({
    $case: z.literal(EntityType.DATE),
    value: z.object({}),
  })
  .describe('Calendar date entity');

export type DateConfig = z.infer<typeof DateEntitySchema>;

export const EntityConfigSchema = z
  .discriminatedUnion('$case', [
    NumberSchema,
    TimeSchema,
    PhoneNumberSchema,
    DateEntitySchema,
    AlphanumericSchema,
    MultipleOptionsSchema,
    AddressSchema,
    FreeTextSchema,
    NameSchema,
  ])
  .describe(
    'Type-specific validation configuration, discriminated by entity type',
  );

export type EntityConfig = z.infer<typeof EntityConfigSchema>;

// Main entity schema
export const EntitySchema = EntityBaseSchema.extend({
  name: EntityNameSchema,
  description: EntityDescriptionSchema,
  type: EntityTypeEnums,
  references: EntityReferencesSchema,
  config: EntityConfigSchema.optional(),
}).merge(EntityMetadataSchema);

export type Entity = z.infer<typeof EntitySchema>;
